<!-- src/components/Layout/ChangePinModal.vue -->
<!-- The signed-in person's PIN for the AI phone line, from the user menu (docs/features/ai_phone_personal_pins_FEATURE.md). -->
<template>
  <GenericModal title="Change your PIN" max-width="max-w-md" @close="$emit('close')">
    <p class="text-sm text-gray-600">
      When you call the AI phone line from your own phone, you key this PIN so Sunna, Aurora and
      Vesta know it is you.
    </p>
    <p v-if="statusLine" class="mt-3 text-sm font-medium text-gray-800" data-testid="pin-status">
      {{ statusLine }}
    </p>

    <form class="mt-4 space-y-3" @submit.prevent="save">
      <div>
        <label for="phone-pin-new" class="mb-1 block text-sm font-medium text-gray-700"
          >New PIN</label
        >
        <input
          id="phone-pin-new"
          :value="pin"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          class="form-input w-full tracking-widest"
          @input="pin = digits($event)"
        />
      </div>
      <div>
        <label for="phone-pin-again" class="mb-1 block text-sm font-medium text-gray-700"
          >Enter it again</label
        >
        <input
          id="phone-pin-again"
          :value="confirmation"
          type="password"
          inputmode="numeric"
          autocomplete="new-password"
          class="form-input w-full tracking-widest"
          @input="confirmation = digits($event)"
        />
      </div>
      <p class="text-xs text-gray-500">
        Six digits. Avoid easy ones like 123456, 121212 or the end of your own phone number.
      </p>
      <p v-if="error" class="text-sm text-red-600" role="alert">{{ error }}</p>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn-secondary" @click="$emit('close')">Cancel</button>
        <button type="button" class="btn-primary" :disabled="!canSave" @click="save">
          {{ saving ? 'Saving...' : 'Save PIN' }}
        </button>
      </div>
    </template>
  </GenericModal>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import GenericModal from '@/components/Common/GenericModal.vue'
import { usePhonePin } from '@/composables/usePhonePin'
import { useToast } from '@/composables/useToast'

const emit = defineEmits(['close'])
const toast = useToast()
const { status, loading, saving, error, loadStatus, savePin } = usePhonePin()
const pin = ref('')
const confirmation = ref('')

// Digits only, six at most. No maxlength on the inputs: it would cut a pasted "483-920" before the separators came out.
const digits = event => {
  const value = event.target.value.replace(/\D/g, '').slice(0, 6)
  event.target.value = value
  return value
}
const statusLine = computed(() => {
  if (loading.value) return 'Checking your PIN...'
  if (!status.value) return '' // the check failed: the error says why
  if (!status.value.set) return 'You have not set a PIN yet.'
  const when = status.value.setAt
    ? new Date(status.value.setAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null
  return when ? `Your PIN was set on ${when}.` : 'Your PIN is set.'
})
const canSave = computed(
  () => pin.value.length === 6 && confirmation.value.length === 6 && !saving.value
)

async function save() {
  if (!canSave.value) return
  if (await savePin(pin.value, confirmation.value)) {
    toast.success('Your PIN is saved.')
    emit('close')
  }
}

onMounted(loadStatus)
</script>
